<script setup lang="ts">
import type { MenuItemInterface } from "~~/server/types/menu";

defineProps<{
  item: MenuItemInterface;
  idx: number;
}>();
</script>

<template>
  <div class="panel panel--custom_dropdown" :id="`panel-${idx}`" role="menu">
    <ul class="drop-list">
      <li
          v-for="(c, ck) in item.children"
          :key="ck"
          class="drop-item"
          style="margin: 0 -10px; padding:0 10px"
          :class="{ 'has-children': c.children?.length }"
      >
        <NuxtLinkLocale :to="c.url" class="drop-link">
          <span class="drop-link-left">
            <img
                v-if="c.icon"
                class="image-dropdown"
                :src="c.icon"
                :alt="c.name"
                loading="lazy"
                decoding="async"
                width="44"
                height="44"
            >
            <span class="drop-link-text">{{ c.name }}</span>
          </span>

          <Icon
              v-if="c.children?.length"
              name="material-symbols-light:chevron-right-rounded"
              size="20"
              class="drop-arrow"
          />
        </NuxtLinkLocale>

        <ClientOnly>
          <div
              v-if="c.children?.length"
              class="drop-sub-list panel panel--custom_dropdown"
              role="menu"
          >
            <div class="drop-sub-list-container">
              <ul class="drop-list">
                <li
                    v-for="(cc, cck) in c.children"
                    :key="cck"
                    class="drop-item drop-item--sub"
                >
                  <NuxtLinkLocale :to="cc.url" class="drop-link">
                    <span class="drop-link-left">
                      <img
                          v-if="cc.icon"
                          class="image-dropdown image-dropdown--small"
                          :src="cc.icon"
                          :alt="cc.name"
                          loading="lazy"
                          decoding="async"
                          width="28"
                          height="28"
                      >
                      <span class="drop-link-text">{{ cc.name }}</span>
                    </span>
                  </NuxtLinkLocale>
                </li>
              </ul>
            </div>
          </div>
        </ClientOnly>
      </li>
    </ul>
  </div>
</template>

<style scoped lang="scss">
.panel--custom_dropdown {
  min-width: 260px;
  padding: 8px;
  background: var(--nav-panel-bg);
  color: var(--nav-panel-text);
  border: 1px solid rgba(0, 0, 0, 0.06);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);

  .drop-list {
    display: grid;
    gap: 4px;
    padding: 0;
    margin: 0;
    list-style: none;
  }

  .drop-item {
    position: relative;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .drop-link {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    width: 100%;
    padding: 10px 12px;
    border-radius: 14px;
    color: inherit;
    text-decoration: none;
    transition:
        background 0.2s ease,
        transform 0.2s ease,
        box-shadow 0.2s ease;
  }

  .drop-link:hover {
    background: linear-gradient(135deg, rgba(0, 0, 0, 0.05), rgba(0, 0, 0, 0.025));
    transform: translateX(2px);
  }

  .drop-link-left {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    flex: 1;
  }

  .drop-link-text {
    font-size: 14px;
    font-weight: 500;
    line-height: 1.25;
    word-break: break-word;
  }

  .image-dropdown {
    width: 44px;
    height: 44px;
    flex: 0 0 44px;
    object-fit: cover;
    border-radius: 12px;
    background: #f6f6f6;
    box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.04);
  }

  .image-dropdown--small {
    width: 28px;
    height: 28px;
    flex: 0 0 28px;
    border-radius: 8px;
  }

  .drop-arrow {
    flex-shrink: 0;
    opacity: 0.7;
    transition: transform 0.2s ease, opacity 0.2s ease;
  }

  .has-children:hover > .drop-link .drop-arrow {
    opacity: 1;
    transform: translateX(2px);
  }

  .drop-sub-list {
    position: absolute;
    top: -8px;
    left: calc(100%);
    min-width: 240px;
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    transform: translateY(4px);
    transition:
        opacity 0.18s ease,
        transform 0.18s ease,
        visibility 0.18s ease;
    z-index: 50;
  }

  .drop-sub-list-container {
    padding: 0;
  }

  .drop-item:hover > .drop-sub-list {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
    transform: translateY(0);
  }

  .drop-item--sub .drop-link {
    padding: 9px 12px;
  }
}
</style>